import React from 'react';
import { useInView } from '../hooks/useInView';
import { Globe, Shield, TrendingUp } from 'lucide-react';

const sourcingPoints = [
  {
    icon: Globe,
    title: 'Multi-Region Partners',
    description: 'We have deep experience in China, with additional long-term partners across Vietnam, Bangladesh, and Egypt.',
  },
  {
    icon: Shield,
    title: 'Compliance & Quality',
    description: 'We focus on quality systems, material consistency, and on-time delivery expectations.',
  },
  {
    icon: TrendingUp,
    title: 'Flexible Capacity',
    description: 'We scale programs up or down depending on seasonality and channel needs.',
  },
];

// Orbital node configuration - each node orbits at different speeds and radii
const orbitalNodes = [
  { radius: 140, duration: 35, startAngle: 0, size: 6, opacity: 0.4 },
  { radius: 140, duration: 35, startAngle: 180, size: 5, opacity: 0.3 },
  { radius: 110, duration: 28, startAngle: 90, size: 7, opacity: 0.5 },
  { radius: 110, duration: 28, startAngle: 270, size: 4, opacity: 0.25 },
  { radius: 80, duration: 22, startAngle: 45, size: 5, opacity: 0.35 },
  { radius: 80, duration: 22, startAngle: 225, size: 6, opacity: 0.4 },
  { radius: 170, duration: 40, startAngle: 135, size: 4, opacity: 0.2 },
];

const GlobalNetworkVisualization = () => {
  return (
    <div
      className="relative h-[400px] rounded-2xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Subtle radial gradient backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.03) 0%, transparent 60%)',
        }}
      />

      {/* Center point with soft glow */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-3 h-3 rounded-full"
          style={{
            background: 'rgba(255,255,255,0.3)',
            boxShadow: '0 0 20px 8px rgba(255,255,255,0.1)',
          }}
        />
      </div>

      {/* Concentric orbital rings */}
      <div className="absolute inset-0 flex items-center justify-center">
        {[80, 110, 140, 170].map((radius, index) => (
          <div
            key={radius}
            className="absolute rounded-full"
            style={{
              width: radius * 2,
              height: radius * 2,
              border: `1px solid rgba(255,255,255,${0.04 - index * 0.005})`,
            }}
          />
        ))}
      </div>

      {/* Animated orbital nodes */}
      <div className="absolute inset-0 flex items-center justify-center">
        {orbitalNodes.map((node, index) => (
          <div
            key={index}
            className="absolute"
            style={{
              width: node.radius * 2,
              height: node.radius * 2,
              animation: `orbit${index} ${node.duration}s linear infinite`,
            }}
          >
            {/* The node itself */}
            <div
              className="absolute rounded-full"
              style={{
                width: node.size,
                height: node.size,
                background: `rgba(255,255,255,${node.opacity})`,
                boxShadow: `0 0 ${node.size * 2}px ${node.size / 2}px rgba(255,255,255,${node.opacity * 0.5})`,
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />
          </div>
        ))}
      </div>

      {/* Subtle connection lines - SVG overlay */}
      <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.15 }}>
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.5" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Animated connection lines */}
        <line
          x1="50%" y1="50%" x2="30%" y2="25%"
          stroke="url(#lineGradient)"
          strokeWidth="1"
          style={{
            animation: 'fadeLineA 8s ease-in-out infinite',
          }}
        />
        <line
          x1="50%" y1="50%" x2="70%" y2="35%"
          stroke="url(#lineGradient)"
          strokeWidth="1"
          style={{
            animation: 'fadeLineB 10s ease-in-out infinite',
            animationDelay: '2s',
          }}
        />
        <line
          x1="50%" y1="50%" x2="65%" y2="70%"
          stroke="url(#lineGradient)"
          strokeWidth="1"
          style={{
            animation: 'fadeLineA 12s ease-in-out infinite',
            animationDelay: '4s',
          }}
        />
        <line
          x1="50%" y1="50%" x2="35%" y2="65%"
          stroke="url(#lineGradient)"
          strokeWidth="1"
          style={{
            animation: 'fadeLineB 9s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />
      </svg>

      {/* Label */}
      <div className="absolute bottom-6 left-6">
        <p className="text-white/30 text-[12px] uppercase tracking-wider">Global Network</p>
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes orbit0 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbit1 {
          from { transform: rotate(180deg); }
          to { transform: rotate(540deg); }
        }
        @keyframes orbit2 {
          from { transform: rotate(90deg); }
          to { transform: rotate(450deg); }
        }
        @keyframes orbit3 {
          from { transform: rotate(270deg); }
          to { transform: rotate(630deg); }
        }
        @keyframes orbit4 {
          from { transform: rotate(45deg); }
          to { transform: rotate(405deg); }
        }
        @keyframes orbit5 {
          from { transform: rotate(225deg); }
          to { transform: rotate(585deg); }
        }
        @keyframes orbit6 {
          from { transform: rotate(135deg); }
          to { transform: rotate(495deg); }
        }
        @keyframes fadeLineA {
          0%, 100% { opacity: 0; }
          30%, 70% { opacity: 1; }
        }
        @keyframes fadeLineB {
          0%, 100% { opacity: 0; }
          40%, 60% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const GlobalSourcing = () => {
  const [ref, isInView] = useInView({ threshold: 0.15 });

  return (
    <section className="bg-[#0a0a0a] py-20 px-6">
      <div className="max-w-[1320px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div ref={ref}>
            <p className="text-[#6a6a6a] text-[13px] mb-3 uppercase tracking-wider">Global Sourcing</p>
            <h2 className="text-[38px] md:text-[46px] font-extralight text-white mb-6 tracking-[-0.02em]">
              Sourcing Beyond Borders
            </h2>
            <p className="text-[#8a8a8a] text-[16px] leading-[1.7] mb-10">
              Our sourcing goes beyond one region. It is built through long-standing factory relationships and flexible capacity.
            </p>

            {/* Sourcing points */}
            <div className="space-y-6">
              {sourcingPoints.map((point, index) => {
                const Icon = point.icon;
                return (
                  <div
                    key={point.title}
                    className="flex gap-4"
                    style={{
                      opacity: isInView ? 1 : 0,
                      transform: isInView ? 'translateX(0)' : 'translateX(-20px)',
                      transition: `opacity 0.6s ease ${index * 150}ms, transform 0.6s ease ${index * 150}ms`,
                    }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-white/60" />
                    </div>
                    <div>
                      <h4 className="text-white text-[16px] font-medium mb-1">{point.title}</h4>
                      <p className="text-[#8a8a8a] text-[14px] leading-[1.6]">{point.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual - Abstract Network Visualization */}
          <GlobalNetworkVisualization />
        </div>
      </div>
    </section>
  );
};

export default GlobalSourcing;
